import BossModel from "@/models/BossModel";
import MediaLibraryImage from '@/models/Media/Library/Item/Image/MediaLibraryImage';
import MediaLibraryDocument from '@/models/Media/Library/Item/Document/MediaLibraryDocument';
import MediaLibraryPdf from '@/models/Media/Library/Item/Pdf/MediaLibraryPdf';
import MediaLibraryVector from '@/models/Media/Library/Item/Vector/MediaLibraryVector';
import MediaLibraryVideo from '@/models/Media/Library/Item/Video/MediaLibraryVideo';
import MediaLibraryFolder from '@/models/Media/Library/Folder/MediaLibraryFolder';
import MediaLibraryTag from "@/models/Media/Library/Tag/MediaLibraryTag";
import MediaLibraryItemMediaLibraryTag from "@/models/Media/Library/Item/MediaLibraryItemMediaLibraryTag";
import { saveAs } from 'file-saver';

export default class MediaLibraryItem extends BossModel {

    /**
     *
     * @type {string}
     */
    static apiUrl = 'media-library-item';

    /**
     *
     * @type {string}
     */
    static entity = 'mediaLibraryItems';

    static fields() {
        return {
            id: this.number(null).nullable(),
            name: this.attr(null),
            path: this.attr(null),
            size: this.attr(null),
            filename: this.attr(null),
            media_library_folder_id: this.number(null).nullable(),
            embedding_status: this.attr(null).nullable(),
            deleted_at: this.attr(null),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            mediaLibraryFolder: this.belongsTo(MediaLibraryFolder, ''),
            mediaLibraryDocument: this.hasOne(MediaLibraryDocument, 'media_library_item_id'),
            mediaLibraryImage: this.hasOne(MediaLibraryImage, 'media_library_item_id'),
            mediaLibraryPdf: this.hasOne(MediaLibraryPdf, 'media_library_item_id'),
            mediaLibraryVector: this.hasOne(MediaLibraryVector, 'media_library_item_id'),
            mediaLibraryVideo: this.hasOne(MediaLibraryVideo, 'media_library_item_id'),
            mediaLibraryTags: this.belongsToMany(
                MediaLibraryTag,
                MediaLibraryItemMediaLibraryTag,
                'media_library_item_id',
                'media_library_tag_id'
            ),
        }
    }

    /**
     *
     * @param name
     * @param parentId
     * @returns {Promise<AxiosResponse<T>>}
     */
    static create(name, parentId = null) {
        return this.api().create({name: name, media_library_folder_parent_id: parentId});
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    update() {
        return this.api().update();
    }

    /**
     *
     * @param itemIds
     * @param folderId
     * @returns {Promise<AxiosResponse<T>>}
     */
    static multiUpdateParentFolder(itemIds, folderId) {
        return this.api().action('multi-update/parent-folder').update({itemIds: itemIds, folderId: folderId}, false);
    }

    /**
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    restore() {
        return this.constructor.api().action('restore/' + this.id).getSingle();
    }

    /**
     *
     * @param term
     * @param tags
     * @param allTags
     * @returns {PromiseLike<[]> | Promise<[]> | *}
     */
    static search(term, tags, allTags) {
        return this.api().action('search')
            .where('term', term)
            .where((allTags ? 'hasAllTags' : 'hasTags'), '[' + tags.join(',') + ']')
            .limit(100)
            .with([
                'mediaLibraryTags',
                'mediaLibraryVideo',
                'mediaLibraryImage',
                'mediaLibraryDocument',
                'mediaLibraryPdfs',
                'mediaLibraryVectors',
            ])
            .get().then((response) => {
                let images = [];
                if (Array.isArray(response) && response.length > 0) {
                    response.forEach((image) => {
                        images.push(new MediaLibraryItem(image));
                    })
                }
                return images;
            });
    }

    /**
     *
     * @param fileName
     * @returns {Promise<*>}
     */
    download(fileName = this.filename ) {
        return this.constructor.api().action(this.id + '/download').getBlob().then(response => {
            return saveAs(response, fileName);
        });
    }

    /**
     *
     * @param fileName
     * @returns {Promise<*>}
     */
    static downloadMultiple(fileIds = [],foldersIds = []) {
        let fileStr = new URLSearchParams(fileIds.map(s=>['items[]',s])).toString();
        let folderStr = new URLSearchParams(foldersIds.map(s=>['folders[]',s])).toString();

        return this.api().action('download-multiple?'+fileStr+'&'+folderStr).getBlob().then(response => {
            return saveAs(response, 'media-library-items.zip');
        });
    }

    /**
     *
     * @param items
     * @returns {Promise<AxiosResponse<T>>}
     */
    static deleteItems(items) {
        return this.api().action('delete-items').updateMultiple({'items': items}, true);
    }

    /**
     * alias since the name is ambiguous
     * @returns {boolean|*}
     */
    Folder() {
        return this.ParentFolder();
    }

    /**
     *
     * @returns {boolean|*}
     */
    ParentFolder() {
        return this.getRelated('mediaLibraryFolder');
    }

    /**
     *
     * @param folderId
     * @returns {Promise<AxiosResponse<T>>}
     */
    moveToFolder(folderId) {
        this.media_library_folder_id = folderId;
        return this.update();
    }

    /**
     *
     * @returns {MediaLibraryVideo|*}
     */
    Video() {
        if (this.mediaLibraryVideo !== undefined && this.mediaLibraryVideo !== null && isNaN(this.mediaLibraryVideo)) {
            return new MediaLibraryVideo(this.mediaLibraryVideo);
        }
        return this.getRelated('mediaLibraryVideo');
    }

    /**
     *
     * @returns {*|MediaLibraryImage}
     */
    Image() {
        if (this.mediaLibraryImage !== undefined && this.mediaLibraryImage !== null && isNaN(this.mediaLibraryImage)) {
            return new MediaLibraryImage(this.mediaLibraryImage);
        }
        return this.getRelated('mediaLibraryImage');
    }

    /**
     *
     * @returns {*}
     */
    Document() {
        if (this.mediaLibraryDocument !== undefined && this.mediaLibraryDocument !== null && isNaN(this.mediaLibraryDocument)) {
            return new MediaLibraryDocument(this.mediaLibraryDocument);
        }
        return this.getRelated('mediaLibraryDocument');
    }

    /**
     *
     * @returns {*}
     */
    Pdf() {
        if (this.mediaLibraryPdf !== undefined && this.mediaLibraryPdf !== null && isNaN(this.mediaLibraryPdf)) {
            return new MediaLibraryPdf(this.mediaLibraryPdf);
        }
        return this.getRelated('mediaLibraryPdf');
    }

    /**
     *
     * @returns {*}
     */
    Vector() {
        if (this.mediaLibraryVector !== undefined && this.mediaLibraryVector !== null && isNaN(this.mediaLibraryVector)) {
            return new MediaLibraryVector(this.mediaLibraryVector);
        }
        return this.getRelated('mediaLibraryVector');
    }

    /**
     *
     * @param tags
     * @returns {Promise<AxiosResponse<T>>}
     */
    syncTags(tags) {
        return this.constructor.api().action(this.id + '/sync-tags').update({'tags': tags}, false).then(() => {
            return this.constructor.api().with(['mediaLibraryTags']).find(this.id);
        });
    }

    /**
     *
     * @param items
     * @param tags
     * @returns {Promise<AxiosResponse<T>>}
     */
    static syncItemsWithTags(items, tags) {
        return this.api().action('sync-items-tags')
            .update({'items': items.map((item) => item.id), 'tags': tags}, false);
    }

    /**
     *
     * @returns {*}
     */
    Tags() {
        return this.getRelated('mediaLibraryTags');
    }

    /**
     *
     * @returns {*}
     */
    getChild() {
        let type = this.getChildType();
        if(!type){
            return null;
        }
        return this[type.charAt(0).toUpperCase() + type.slice(1)]();
    }

    /**
     *
     * @returns {string}
     */
    getChildType() {
        if (this.mediaLibraryImage) {
            return 'image';
        }
        if (this.mediaLibraryVideo) {
            return 'video';
        }
        if (this.mediaLibraryPdf) {
            return 'pdf';
        }
        if (this.mediaLibraryVector) {
            return 'vector';
        }
        if (this.mediaLibraryDocument) {
            return 'document';
        }
        if (this.$query().find(this.id)) {
            if (this.Video()) {
                return 'video';
            }
            if (this.Document()) {
                return 'document';
            }
            if (this.Image()) {
                return 'image';
            }
            if (this.Pdf()) {
                return 'pdf';
            }
            if (this.Vector()) {
                return 'vector';
            }
        }
    }

}
